import { useEffect } from 'react';

interface SeoMetaOptions {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  /// Keep the page out of search results (not-found pages). The app always answers
  /// 200, so this is how crawlers learn the page doesn't exist.
  noIndex?: boolean;
}

const BASE_URL = 'https://climbingweather.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * Hook to dynamically update page SEO meta tags
 */
export function useSeoMeta({ title, description, canonicalPath, ogImage, noIndex = false }: SeoMetaOptions) {
  useEffect(() => {
    // A noindex page has no canonical URL; other pages drop any robots tag left behind
    if (noIndex) {
      updateMetaTag('name', 'robots', 'noindex');
      document.querySelector('link[rel="canonical"]')?.remove();
    } else {
      document.querySelector('meta[name="robots"]')?.remove();
    }

    // Update document title
    document.title = title;

    // Update meta description
    updateMetaTag('name', 'description', description);

    // Update Open Graph tags
    updateMetaTag('property', 'og:title', title);
    updateMetaTag('property', 'og:description', description);
    updateMetaTag('property', 'og:url', canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL);
    updateMetaTag('property', 'og:image', ogImage || DEFAULT_OG_IMAGE);

    // Update Twitter tags
    updateMetaTag('name', 'twitter:title', title);
    updateMetaTag('name', 'twitter:description', description);
    updateMetaTag('name', 'twitter:url', canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL);
    updateMetaTag('name', 'twitter:image', ogImage || DEFAULT_OG_IMAGE);

    // Update canonical URL
    if (!noIndex) {
      updateCanonicalUrl(canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL);
    }
  }, [title, description, canonicalPath, ogImage, noIndex]);
}

function updateMetaTag(attributeType: 'name' | 'property', attributeValue: string, content: string) {
  let element = document.querySelector(`meta[${attributeType}="${attributeValue}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeType, attributeValue);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function updateCanonicalUrl(url: string) {
  let element = document.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
}

export default useSeoMeta;
